<template>
  <n-modal
    :show="show"
    preset="card"
    title="Crear cuenta"
    style="max-width: 380px"
    :bordered="false"
    :segmented="{ content: true, footer: true }"
    @update:show="onUpdateShow"
  >
    <n-form ref="formRef" :model="formValue" :rules="rules">
      <n-form-item path="nickname" label="Apodo">
        <n-input v-model:value="formValue.nickname" placeholder="Tu apodo" />
      </n-form-item>
      <n-form-item path="email" label="Email">
        <n-input
          v-model:value="formValue.email"
          placeholder="lector@email.com"
        />
      </n-form-item>
      <n-form-item path="password" label="Contraseña">
        <n-input
          v-model:value="formValue.password"
          type="password"
          show-password-on="click"
          placeholder="••••••••"
        />
      </n-form-item>
      <n-form-item path="passwordConfirm" label="Repite la contraseña">
        <n-input
          v-model:value="formValue.passwordConfirm"
          type="password"
          show-password-on="click"
          placeholder="••••••••"
          @keydown.enter.prevent="handleSubmit"
        />
      </n-form-item>
    </n-form>

    <n-alert
      v-if="errorMessage"
      type="error"
      :title="errorMessage"
      closable
      style="margin-bottom: 12px"
      @close="errorMessage = ''"
    />

    <template #footer>
      <div class="modal-footer">
        <button
          type="button"
          class="link-btn"
          @click="$emit('switch-to-login')"
        >
          ¿Ya tienes cuenta? Inicia sesión
        </button>
        <n-button type="primary" :loading="loading" @click="handleSubmit"
          >Crear cuenta</n-button
        >
      </div>
    </template>
  </n-modal>
</template>

<script setup>
import { ref } from "vue";
import { useAuthStore } from "@/store/auth";

defineProps({
  show: { type: Boolean, default: false },
});

const emit = defineEmits(["update:show", "switch-to-login", "success"]);

const authStore = useAuthStore();
const formRef = ref(null);
const loading = ref(false);
const errorMessage = ref("");

const formValue = ref({
  name: "",
  email: "",
  password: "",
  passwordConfirm: "",
});

const rules = {
  name: [
    {
      required: true,
      message: "Introduce tu nombre",
      trigger: ["blur", "input"],
    },
  ],
  email: [
    {
      required: true,
      message: "Introduce tu email",
      trigger: ["blur", "input"],
    },
  ],
  password: [
    {
      required: true,
      message: "Introduce una contraseña",
      trigger: ["blur", "input"],
    },
  ],
  passwordConfirm: [
    {
      required: true,
      message: "Repite la contraseña",
      trigger: ["blur", "input"],
    },
    {
      validator: (rule, value) => value === formValue.value.password,
      message: "Las contraseñas no coinciden",
      trigger: ["blur", "input"],
    },
  ],
};

function resetForm() {
  formValue.value = {
    nickname: "",
    email: "",
    password: "",
    passwordConfirm: "",
  };
  errorMessage.value = "";
}

function onUpdateShow(value) {
  emit("update:show", value);
  if (!value) resetForm();
}

async function handleSubmit() {
  errorMessage.value = "";
  try {
    await formRef.value?.validate();
  } catch {
    return;
  }

  loading.value = true;
  try {
    await authStore.register({
      nickname: formValue.value.nickname,
      email: formValue.value.email,
      password: formValue.value.password,
      matchingPassword: formValue.value.passwordConfirm,
    });
    emit("update:show", false);
    resetForm();
    emit("success");
  } catch (err) {
    errorMessage.value =
      err?.response?.data?.message ||
      err.message ||
      "No se pudo crear la cuenta.";
  } finally {
    loading.value = false;
  }
}
</script>

<style scoped>
.modal-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.link-btn {
  background: none;
  border: none;
  padding: 0;
  font-size: 13px;
  color: var(--ink-soft);
  text-decoration: underline;
  cursor: pointer;
}
.link-btn:hover {
  color: var(--melon-deep);
}
</style>
